import React, { useState, useEffect } from 'react';
import { Bell, Mail, CheckCircle, AlertCircle, Globe } from 'lucide-react';
import './App.css';

const API_URL = 'https://cloudflare-ip-notifier-api.nima-456.workers.dev';

function App() {
  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [loading, setLoading] = useState(false);
  const [currentIPs, setCurrentIPs] = useState({ v4: [], v6: [] });
  const [lastChecked, setLastChecked] = useState(null);

  // Check for verification status from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const verified = params.get('verified');
    
    if (verified === 'true') {
      setMessage({ 
        text: 'Email verified successfully! You will now receive notifications when Cloudflare IPs change.', 
        type: 'success' 
      });
      window.history.replaceState({}, '', '/');
    } else if (verified === 'false') {
      const error = params.get('error');
      const errorMsg = error === 'invalid-token' 
        ? 'Invalid or expired verification link.' 
        : 'Verification failed. Please try subscribing again.';
      setMessage({ text: errorMsg, type: 'error' });
      window.history.replaceState({}, '', '/');
    }

    const unsubscribed = params.get('unsubscribed');

    if (unsubscribed === 'true') {
      setMessage({ 
        text: 'Successfully unsubscribed from notifications.', 
        type: 'success' 
      });
      window.history.replaceState({}, '', '/');
    } else if (unsubscribed === 'false') {
      const error = params.get('error');
      const errorMsg = error === 'invalid-token' 
        ? 'Invalid unsubscribe link.' 
        : 'Unsubscribe failed.';
      setMessage({ text: errorMsg, type: 'error' });
      window.history.replaceState({}, '', '/');
    }
  }, []);

  useEffect(() => {
    fetchCurrentIPs();
  }, []);

  const fetchCurrentIPs = async () => {
    try {
      const [v4Response, v6Response] = await Promise.all([
        fetch('https://cloudflare-ip-notifier-proxy.nima-456.workers.dev/ips-v4'),
        fetch('https://cloudflare-ip-notifier-proxy.nima-456.workers.dev/ips-v6')
      ]);

      const v4Text = await v4Response.text();
      const v6Text = await v6Response.text();

      const v4List = v4Text.trim().split('\n').filter(ip => ip);
      const v6List = v6Text.trim().split('\n').filter(ip => ip);

      setCurrentIPs({ v4: v4List, v6: v6List });
      setLastChecked(new Date().toISOString());
    } catch (error) {
      console.error('Error fetching IPs:', error);
    }
  };

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubscribe = async () => {
    if (!validateEmail(email)) {
      setMessage({ text: 'Please enter a valid email address', type: 'error' });
      return;
    }

    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage({ 
          text: data.message || 'Please check your email to confirm your subscription.', 
          type: 'success' 
        });
      } else {
        setMessage({ text: data.error || 'Subscription failed', type: 'error' });
      }
    } catch (error) {
      setMessage({ text: 'Network error. Please try again.', type: 'error' });
    }
    
    setLoading(false);
  };

  const handleUnsubscribe = async () => {
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/unsubscribe`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setIsSubscribed(false);
        setEmail('');
        setMessage({ text: 'Successfully unsubscribed', type: 'success' });
      } else {
        setMessage({ text: data.error || 'Unsubscribe failed', type: 'error' });
      }
    } catch (error) {
      setMessage({ text: 'Network error. Please try again.', type: 'error' });
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-orange-50">
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <Globe className="w-12 h-12 text-orange-500 mr-3" />
            <h1 className="text-4xl font-bold text-gray-900">
              Cloudflare IP Monitor
            </h1>
          </div>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Get instant email notifications when Cloudflare's IPv4 or IPv6 ranges change. 
            Keep your firewall rules up-to-date automatically.
          </p>
        </div>

        {/* Subscription Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <div className="flex items-center mb-6">
            <Bell className="w-6 h-6 text-blue-600 mr-2" />
            <h2 className="text-2xl font-semibold text-gray-900">
              {isSubscribed ? 'Subscription Active' : 'Subscribe for Notifications'}
            </h2>
          </div>

          {message.text && (
            <div className={`mb-6 p-4 rounded-lg flex items-start ${
              message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
            }`}>
              {message.type === 'success' ? (
                <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
              ) : (
                <AlertCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
              )}
              <span>{message.text}</span>
            </div>
          )}

          {!isSubscribed ? (
            <div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              <button
                onClick={handleSubscribe}
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Subscribing...' : 'Subscribe to Notifications'}
              </button>
            </div>
          ) : (
            <div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <p className="text-green-800">
                  <strong>Subscribed:</strong> {email}
                </p>
              </div>
              <button
                onClick={handleUnsubscribe}
                disabled={loading}
                className="w-full bg-gray-600 hover:bg-gray-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors disabled:opacity-50"
              >
                {loading ? 'Unsubscribing...' : 'Unsubscribe'}
              </button>
            </div>
          )}
        </div>

        {/* Current IPs Display */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">
            Current Cloudflare IP Ranges
          </h2>
          
          {lastChecked && (
            <p className="text-sm text-gray-500 mb-6">
              Last checked: {new Date(lastChecked).toLocaleString()}
            </p>
          )}

          <div className="grid md:grid-cols-2 gap-6">
            {/* IPv4 */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm mr-2">
                  IPv4
                </span>
                {currentIPs.v4.length} ranges
              </h3>
              <div className="bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto">
                {currentIPs.v4.length > 0 ? (
                  <ul className="text-sm text-gray-700 space-y-1 font-mono">
                    {currentIPs.v4.map((ip, index) => (
                      <li key={index}>{ip}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-500">Loading IPv4 ranges...</p>
                )}
              </div>
            </div>

            {/* IPv6 */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded text-sm mr-2">
                  IPv6
                </span>
                {currentIPs.v6.length} ranges
              </h3>
              <div className="bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto">
                {currentIPs.v6.length > 0 ? (
                  <ul className="text-sm text-gray-700 space-y-1 font-mono">
                    {currentIPs.v6.map((ip, index) => (
                      <li key={index}>{ip}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-500">Loading IPv6 ranges...</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-xl p-6">
          <h3 className="font-semibold text-gray-900 mb-2">How it works</h3>
          <ul className="text-sm text-gray-700 space-y-2">
            <li>• We monitor Cloudflare's official IP lists every hour</li>
            <li>• When changes are detected, all subscribers receive an email notification</li>
            <li>• Emails include the complete list of added and removed IP ranges</li>
            <li>• Your email is only used for notifications and is never shared</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default App;